import type { BulkWriteOffSide } from '@billflow/shared';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

const SIDES: { value: BulkWriteOffSide; label: string }[] = [
  { value: 'ar', label: 'Customer invoices' },
  { value: 'ap', label: 'Purchase orders' },
];

interface BulkWriteOffFiltersProps {
  side: BulkWriteOffSide;
  onSideChange: (side: BulkWriteOffSide) => void;
  from: string;
  onFromChange: (value: string) => void;
  to: string;
  onToChange: (value: string) => void;
  maxDollars: string;
  onMaxDollarsChange: (value: string) => void;
  onLoad: () => void;
  isFetching: boolean;
  datesValid: boolean;
  dirty: boolean;
}

/**
 * The filter bar for the bulk write-off tool: AR/AP toggle, From/To date
 * range, per-order max (dollars), and the Load button — plus inline hints for
 * an invalid range or filters that have changed since the last Load.
 */
export function BulkWriteOffFilters({
  side,
  onSideChange,
  from,
  onFromChange,
  to,
  onToChange,
  maxDollars,
  onMaxDollarsChange,
  onLoad,
  isFetching,
  datesValid,
  dirty,
}: BulkWriteOffFiltersProps) {
  return (
    <Card className="mb-6">
      <CardContent className="pt-6">
        <div className="flex flex-wrap items-end gap-4">
          {/* AR/AP toggle */}
          <div>
            <Label className="text-xs text-muted-foreground mb-1.5 block">Applies to</Label>
            <div className="flex items-center rounded-md border bg-muted/40 p-0.5 gap-0.5 w-fit">
              {SIDES.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => onSideChange(s.value)}
                  className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                    side === s.value
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          {/* Date range */}
          <div className="flex items-center gap-2">
            <div>
              <Label htmlFor="bwo-from" className="text-xs text-muted-foreground mb-1.5 block">
                From
              </Label>
              <Input
                id="bwo-from"
                type="date"
                value={from}
                onChange={(e) => onFromChange(e.target.value)}
                className="w-36 h-9 text-sm"
              />
            </div>
            <div>
              <Label htmlFor="bwo-to" className="text-xs text-muted-foreground mb-1.5 block">
                To
              </Label>
              <Input
                id="bwo-to"
                type="date"
                value={to}
                onChange={(e) => onToChange(e.target.value)}
                className="w-36 h-9 text-sm"
              />
            </div>
          </div>

          {/* Per-order max */}
          <div>
            <Label htmlFor="bwo-max" className="text-xs text-muted-foreground mb-1.5 block">
              Per-order max
            </Label>
            <div className="relative w-28">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                $
              </span>
              <Input
                id="bwo-max"
                type="number"
                inputMode="decimal"
                min={0.01}
                max={50}
                step={0.01}
                value={maxDollars}
                onChange={(e) => onMaxDollarsChange(e.target.value)}
                className="h-9 text-sm pl-6"
              />
            </div>
          </div>

          {/* Load */}
          <Button onClick={onLoad} disabled={!datesValid} className="h-9">
            {isFetching ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Loading…
              </>
            ) : (
              'Load'
            )}
          </Button>
        </div>

        {!datesValid && (
          <p className="mt-3 text-sm text-destructive">
            &ldquo;From&rdquo; must be on or before &ldquo;To&rdquo;.
          </p>
        )}
        {datesValid && dirty && (
          <p className="mt-3 text-sm text-amber-600 dark:text-amber-400">
            Filters changed — click Load to refresh the results.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
